// @flow
import React from 'react';
import { useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';

import Box from 'bizom-mui/lib/Box';
import TextLink from 'bizom-mui/lib/TextLink';

import { isCordovaApp } from 'utils/isCordovaApp';
import OTPAutoReadDialog from './OTPAutoReadDialog';
import FormikTextField from 'components/FormikTextField';
import {
  PROCEED,
  RESENDING,
  ENTER_OTP,
  RESEND_OTP,
  RESEND_OTP_IN,
  HAVING_TROUBLE,
  CONTACT_SUPPORT,
  ENTER_THE_6_DIGITS_OTP_SEND_TO_YOUR_MOBILE_NUMBER,
} from 'constants/strings';

type Props = {
  phoneno: string,
  resendState: {
    count: number,
    ts: number,
  },
  validating: boolean,
  resending: boolean,
  onSubmit: (otp: string) => void,
  onResend: () => void,
};

const RESEND_INTERVAL = 60; // in seconds
const MAX_RETRY = 3;

const OTPVerificationForm = ({
  phoneno,
  resendState,
  validating,
  resending,
  onResend,
  onSubmit,
}: Props) => {
  const [otp, setOtp] = useState('');
  const [counter, setCounter] = useState(0);
  const [remainingResend, setRemainingResend] = useState(3);
  const [openAutoReadDialog, setOpenAutoReadDialog] = useState(true);
  const isAutoReadEnable = isCordovaApp() && !!window.SMSRetriever;

  if (counter > 0) {
    setTimeout(() => {
      setCounter(counter - 1);
    }, 1000);
  }

  useEffect(() => {
    if (resendState) {
      const diffInSec = Math.floor(
        (new Date().getTime() - resendState.ts) / 1000
      );
      setCounter(RESEND_INTERVAL - diffInSec);
      setRemainingResend(MAX_RETRY - resendState.count);
    }
  }, [resendState]);

  return (
    <div>
      <Formik
        initialValues={{
          otp: '',
        }}
        validate={(values) => {
          setOtp(values.otp);
          return {};
        }}
        onSubmit={(values, actions) => {
          onSubmit(values.otp);
          actions.setSubmitting(false);
        }}
        autoComplete="off"
      >
        {({ values, setFieldValue, isSubmitting, submitForm }) => (
          <React.Fragment>
            <Form>
              <Typography variant="body2">
                {ENTER_THE_6_DIGITS_OTP_SEND_TO_YOUR_MOBILE_NUMBER}
                {` ${phoneno || ''}`}
              </Typography>
              <Box pt={3}>
                <FormikTextField
                  autoFocus
                  fullWidth
                  id="cy-otp-verification-form-otp-field"
                  type="tel"
                  name="otp"
                  label={ENTER_OTP}
                  margin="normal"
                  variant="outlined"
                />
              </Box>
              {remainingResend > 0 && (
                <>
                  {counter > 0 ? (
                    <Typography variant="body2">
                      {RESEND_OTP_IN}
                      {` 00:${('0' + counter).slice(-2)}`}
                    </Typography>
                  ) : resending ? (
                    <Typography variant="body2">{RESENDING}...</Typography>
                  ) : (
                    <Typography
                      variant="body2"
                      color="primary"
                      onClick={onResend}
                    >
                      {RESEND_OTP}
                    </Typography>
                  )}
                </>
              )}
              <Box pt={3}>
                <Grid container justify="flex-end">
                  <Button
                    id="gtm-button-proceed-login-otp cy-otp-verification-form-submit"
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={
                      validating || resending || !otp || otp.length !== 6
                    }
                  >
                    {PROCEED}
                  </Button>
                </Grid>
              </Box>
            </Form>
            {isAutoReadEnable && (
              <OTPAutoReadDialog
                open={openAutoReadDialog}
                onClose={() => setOpenAutoReadDialog(false)}
                onSetOTP={(otp) => setFieldValue('otp', otp)}
                onSubmit={() => submitForm()}
                disabled={isSubmitting}
              />
            )}
          </React.Fragment>
        )}
      </Formik>
      {process.env.REACT_APP_RETAILER_APP_CONTACT_NUMBER && (
        <Box mt={9} align="center">
          <Typography variant="body2" display="inline">
            {HAVING_TROUBLE}
          </Typography>
          <Box ml={1} inline>
            <TextLink
              id="link-contact-support"
              color="primary"
              href={`tel:${process.env.REACT_APP_RETAILER_APP_CONTACT_NUMBER}`}
            >
              {CONTACT_SUPPORT}
            </TextLink>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default OTPVerificationForm;
